/**
 * Generischer CRUD-Hook fuer Stammdaten-/Katalog-Grids (Muster Admin-Seite):
 * Liste + Save-/Delete-Mutationen + Dialog-State in einem Paket.
 * Wird von /admin/stammdaten, /schulungen, /vorsorge und /dokumente genutzt.
 */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { apiError } from "@/services/api"
import type { Page } from "@/types"

export interface CrudApi<T extends { id: string }, C> {
  list: (params?: { size?: number }) => Promise<Page<T>>
  create: (body: C) => Promise<T>
  update: (id: string, body: Partial<C>) => Promise<T>
  remove: (id: string) => Promise<void>
}

export function useMasterData<T extends { id: string }, C>(
  key: string,
  crud: CrudApi<T, C>,
  entityLabel: string
) {
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: [key], queryFn: () => crud.list({ size: 100 }) })
  const [dialog, setDialog] = useState<{ open: boolean; row: T | null }>({
    open: false,
    row: null,
  })

  const save = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: C }) =>
      id ? crud.update(id, body) : crud.create(body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [key] })
      toast.success(vars.id ? `${entityLabel} aktualisiert` : `${entityLabel} angelegt`)
      setDialog({ open: false, row: null })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const del = useMutation({
    mutationFn: (id: string) => crud.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [key] })
      toast.success(`${entityLabel} gelöscht`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return {
    rows: query.data?.data ?? [],
    isLoading: query.isLoading,
    dialog,
    setDialog,
    save,
    del,
    confirmDelete: (row: T) => {
      if (window.confirm(`${entityLabel} wirklich löschen?`)) del.mutate(row.id)
    },
  }
}
